import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Copy,
  Plus,
  RotateCcw,
  Share2,
  Trash2,
  Repeat,
  ArrowLeft,
} from "lucide-react";
import {
  americanError,
  computeRoundRobin,
  formatAmerican,
  formatDecimal,
  formatUSD,
  LEG_STATUS,
  nCr,
} from "@/lib/oddsMath";
import { ROUND_ROBIN } from "@/constants/testIds";
import { EVENTS, track } from "@/lib/analytics";
import { useSEO } from "@/lib/useSEO";

const MIN_LEGS = 3;
const MAX_LEGS = 12;
const initialLegs = () => ["", "", ""];

const TITLE = "Round Robin Calculator – Bet Cost & Payouts";
const DESCRIPTION =
  "Free round robin calculator. Enter 3-12 selections, choose combination sizes, split by amount per combo or total budget, and see stakes, payouts, and graded results including push and void handling.";

const STATUS_OPTIONS = [
  { value: LEG_STATUS.NOT_DECIDED, label: "Not Decided" },
  { value: LEG_STATUS.WON, label: "Won" },
  { value: LEG_STATUS.LOST, label: "Lost" },
  { value: LEG_STATUS.PUSH, label: "Push" },
  { value: LEG_STATUS.VOID, label: "Void" },
];

const wagerErrorText = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const trimmed = String(raw).trim();
  if (!/^\d*\.?\d*$/.test(trimmed)) return "Enter a positive dollar amount.";
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return "Enter a valid amount.";
  if (n <= 0) return "Amount must be greater than zero.";
  return "";
};

const STATUS_BADGE = {
  won: { bg: "#dcfce7", color: "#047857", label: "Won" },
  lost: { bg: "#fee2e2", color: "#b91c1c", label: "Lost" },
  pushed: { bg: "#e0f2fe", color: "#075985", label: "Refund" },
  pending: { bg: "#f1f5f9", color: "#334155", label: "Pending" },
};

const StatusBadge = ({ status, testid }) => {
  const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
};

export default function RoundRobinCalculator() {
  useSEO({ title: TITLE, description: DESCRIPTION, path: "/round-robin-calculator" });

  const [legs, setLegs] = useState(initialLegs);
  const [legStatuses, setLegStatuses] = useState([
    LEG_STATUS.NOT_DECIDED,
    LEG_STATUS.NOT_DECIDED,
    LEG_STATUS.NOT_DECIDED,
  ]);
  const [selectedSizes, setSelectedSizes] = useState([2]);
  const [wagerMode, setWagerMode] = useState("per_combo");
  const [wagerValue, setWagerValue] = useState("");

  // Fire "opened" event once when the page mounts.
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    track(EVENTS.ROUND_ROBIN_OPENED, {});
  }, []);

  const legErrors = legs.map((l) => americanError(l));
  const visibleLegErrors = legs.map((l, i) =>
    String(l).trim() === "" ? "" : legErrors[i],
  );
  const wagerErr = wagerErrorText(wagerValue);
  const visibleWagerErr = wagerValue.trim() === "" ? "" : wagerErr;

  const cleanedLegs = useMemo(
    () => legs.map((l) => Number(String(l).trim())),
    [legs],
  );

  // Available combination sizes are 2..n-1 (a "by n" would just be one parlay of all).
  const availableSizes = useMemo(() => {
    const out = [];
    for (let k = 2; k <= Math.max(2, legs.length - 1); k += 1) out.push(k);
    return out;
  }, [legs.length]);

  // Prune selected sizes when leg count shrinks.
  useEffect(() => {
    setSelectedSizes((prev) => {
      const filtered = prev.filter((k) => k <= legs.length - 1 && k >= 2);
      if (filtered.length === 0 && legs.length >= 3) return [2];
      return filtered;
    });
  }, [legs.length]);

  const totalCombos = useMemo(
    () => selectedSizes.reduce((acc, k) => acc + nCr(legs.length, k), 0),
    [selectedSizes, legs.length],
  );

  const canCompute =
    legs.length >= MIN_LEGS &&
    legs.length <= MAX_LEGS &&
    legErrors.every((e) => e === "") &&
    legs.every((l) => String(l).trim() !== "") &&
    selectedSizes.length > 0 &&
    !wagerErr &&
    wagerValue !== "" &&
    Number(wagerValue) > 0;

  const result = useMemo(() => {
    if (!canCompute) return null;
    return computeRoundRobin({
      americanLegs: cleanedLegs,
      sizes: selectedSizes,
      wagerMode,
      wagerValue: Number(wagerValue),
      legStatuses,
    });
  }, [canCompute, cleanedLegs, selectedSizes, wagerMode, wagerValue, legStatuses]);

  // Debounced calculated analytics event.
  const fireRef = useRef(null);
  useEffect(() => {
    if (fireRef.current) clearTimeout(fireRef.current);
    if (!result) return undefined;
    fireRef.current = setTimeout(() => {
      track(EVENTS.ROUND_ROBIN_CALCULATED, {
        legs_count: result.n,
        sizes: result.sizes,
        total_combos: result.totalCombos,
        wager_mode: result.wagerMode,
        overall_status: result.overallStatus,
      });
    }, 600);
    return () => {
      if (fireRef.current) clearTimeout(fireRef.current);
    };
  }, [result]);

  const updateLeg = (i, val) => setLegs((prev) => prev.map((l, idx) => (idx === i ? val : l)));
  const updateStatus = (i, val) => {
    setLegStatuses((prev) => prev.map((s, idx) => (idx === i ? val : s)));
    track(EVENTS.ROUND_ROBIN_STATUS_CHANGED, { leg_index: i, status: val });
  };

  const addLeg = () => {
    if (legs.length >= MAX_LEGS) {
      toast.info(`Maximum ${MAX_LEGS} selections.`);
      return;
    }
    setLegs((prev) => [...prev, ""]);
    setLegStatuses((prev) => [...prev, LEG_STATUS.NOT_DECIDED]);
    track(EVENTS.ROUND_ROBIN_LEG_ADDED, { legs_count: legs.length + 1 });
  };

  const removeLeg = (i) => {
    if (legs.length <= MIN_LEGS) {
      toast.info(`A round robin needs at least ${MIN_LEGS} selections.`);
      return;
    }
    setLegs((prev) => prev.filter((_, idx) => idx !== i));
    setLegStatuses((prev) => prev.filter((_, idx) => idx !== i));
    track(EVENTS.ROUND_ROBIN_LEG_REMOVED, { legs_count: legs.length - 1 });
  };

  const toggleSize = (k) => {
    setSelectedSizes((prev) => {
      const next = prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k].sort((a, b) => a - b);
      track(EVENTS.ROUND_ROBIN_SIZE_TOGGLED, { size: k, selected: !prev.includes(k) });
      return next;
    });
  };

  const changeWagerMode = (m) => {
    if (m === wagerMode) return;
    setWagerMode(m);
    track(EVENTS.ROUND_ROBIN_WAGER_MODE_CHANGED, { mode: m });
  };

  const reset = () => {
    setLegs(initialLegs());
    setLegStatuses([LEG_STATUS.NOT_DECIDED, LEG_STATUS.NOT_DECIDED, LEG_STATUS.NOT_DECIDED]);
    setSelectedSizes([2]);
    setWagerMode("per_combo");
    setWagerValue("");
    track(EVENTS.ROUND_ROBIN_RESET, {});
    toast.success("Round robin reset.");
  };

  const resultsText = () => {
    if (!result) return "";
    const lines = [
      "Parlay Assistant — Round Robin",
      `Selections: ${result.n}`,
      `Sizes: ${result.sizes.map((s) => `by ${s}`).join(", ")}`,
      `Combinations: ${result.totalCombos}`,
      `Wager mode: ${result.wagerMode === "per_combo" ? "Amount per combination" : "Total budget"}`,
      `Total stake: ${formatUSD(result.totalStake)}`,
      `Max potential return: ${formatUSD(result.totalMaxReturn)}`,
      `Max potential profit: ${formatUSD(result.totalMaxProfit)}`,
    ];
    if (result.overallStatus === "settled") {
      lines.push(`Settled return: ${formatUSD(result.settledReturn)}`);
      lines.push(`Settled profit: ${formatUSD(result.settledProfit)}`);
    } else if (result.settledReturn > 0 || result.settledStake > 0) {
      lines.push(`Settled so far: ${formatUSD(result.settledReturn)}`);
      lines.push(`Still live max: ${formatUSD(result.pendingReturnMax)}`);
    }
    return lines.join("\n");
  };

  const copyResults = async () => {
    if (!result) {
      toast.error("Enter valid selections and a wager first.");
      return;
    }
    try {
      await navigator.clipboard.writeText(resultsText());
      toast.success("Results copied to clipboard.");
    } catch {
      toast.error("Copy failed. Please try again.");
    }
  };

  const shareResults = async () => {
    if (!result) {
      toast.error("Enter valid selections and a wager first.");
      return;
    }
    const text = resultsText();
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Parlay Assistant — Round Robin",
          text,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        });
      } catch {
        /* user canceled */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Sharing not supported here. Results copied instead.");
    } catch {
      toast.error("Sharing not available in this browser.");
    }
  };

  const showResultsDisabled = !result;

  return (
    <main
      data-testid={ROUND_ROBIN.page}
      className="max-w-6xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
    >
      <div className="mb-6 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="pa-nav-link inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
        >
          <ArrowLeft size={14} /> Back to Calculators
        </Link>
      </div>

      <section className="pa-card p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="font-display text-emerald-700 text-xs tracking-[0.2em] uppercase">
              Tool 03
            </p>
            <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-slate-900 mt-1">
              Round Robin Calculator
            </h1>
            <p className="text-slate-600 mt-2 text-sm md:text-base max-w-xl">
              Enter 3-12 selections and choose how they group into smaller parlays. See total stake,
              per-combo payouts, and graded results.
            </p>
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-xs md:text-sm font-mono-num">
            <Repeat size={16} />
            <span data-testid={ROUND_ROBIN.legsCount}>
              {legs.length} / {MAX_LEGS} selections
            </span>
          </div>
        </div>

        {/* Selections */}
        <div className="mt-7">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Selections · American Odds
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={addLeg}
                disabled={legs.length >= MAX_LEGS}
                data-testid={ROUND_ROBIN.addLeg}
                className="pa-btn pa-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={16} /> Add Selection
              </button>
              <button
                type="button"
                onClick={reset}
                data-testid={ROUND_ROBIN.reset}
                className="pa-btn pa-btn-ghost"
                aria-label="Reset round robin"
              >
                <RotateCcw size={16} /> Reset
              </button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {legs.map((leg, i) => {
              const err = visibleLegErrors[i];
              return (
                <div key={i} className="pa-card-soft-wrap">
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1">
                      <label
                        htmlFor={`rr-leg-${i}`}
                        className="block text-xs font-medium text-slate-600"
                      >
                        Selection {i + 1}
                      </label>
                      <input
                        id={`rr-leg-${i}`}
                        data-testid={ROUND_ROBIN.legInput(i)}
                        value={leg}
                        onChange={(e) => updateLeg(i, e.target.value)}
                        placeholder="e.g. -110 or +150"
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        data-invalid={err ? "true" : "false"}
                        aria-invalid={!!err}
                        className="pa-input font-mono-num mt-1"
                      />
                    </div>
                    <div className="min-w-[140px]">
                      <label
                        htmlFor={`rr-leg-${i}-status`}
                        className="block text-xs font-medium text-slate-600"
                      >
                        Status
                      </label>
                      <select
                        id={`rr-leg-${i}-status`}
                        data-testid={ROUND_ROBIN.legStatus(i)}
                        value={legStatuses[i]}
                        onChange={(e) => updateStatus(i, e.target.value)}
                        className="pa-input mt-1"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLeg(i)}
                      disabled={legs.length <= MIN_LEGS}
                      data-testid={`${ROUND_ROBIN.removeLeg}-${i}`}
                      aria-label={`Remove selection ${i + 1}`}
                      className="pa-btn pa-btn-ghost !px-3 self-end disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {err && (
                    <p data-testid={ROUND_ROBIN.legError(i)} className="pa-inline-error">
                      {err}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sizes */}
        <div className="mt-7">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Round Robin Sizes
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Pick one or more combination sizes. &quot;By 2&quot; makes every 2-leg parlay, &quot;By 3&quot; every 3-leg
            parlay, and so on.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {availableSizes.map((k) => {
              const active = selectedSizes.includes(k);
              const count = nCr(legs.length, k);
              return (
                <button
                  key={k}
                  type="button"
                  data-testid={ROUND_ROBIN.sizeToggle(k)}
                  onClick={() => toggleSize(k)}
                  className="pa-chip"
                  style={{
                    background: active ? "#065f46" : undefined,
                    color: active ? "#ecfdf5" : undefined,
                    borderColor: active ? "#065f46" : undefined,
                  }}
                >
                  By {k} · {count} {count === 1 ? "combo" : "combos"}
                </button>
              );
            })}
          </div>
        </div>

        {/* Wager */}
        <div className="mt-7 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Wager Mode
            </p>
            <div className="mt-2 inline-flex rounded-lg border border-slate-200 overflow-hidden">
              <button
                type="button"
                data-testid={ROUND_ROBIN.wagerModePer}
                onClick={() => changeWagerMode("per_combo")}
                className="px-3 py-2 text-xs font-semibold tracking-wide uppercase"
                style={{
                  background: wagerMode === "per_combo" ? "#065f46" : "#fff",
                  color: wagerMode === "per_combo" ? "#ecfdf5" : "#334155",
                }}
              >
                Amount per Combo
              </button>
              <button
                type="button"
                data-testid={ROUND_ROBIN.wagerModeBudget}
                onClick={() => changeWagerMode("total_budget")}
                className="px-3 py-2 text-xs font-semibold tracking-wide uppercase"
                style={{
                  background: wagerMode === "total_budget" ? "#065f46" : "#fff",
                  color: wagerMode === "total_budget" ? "#ecfdf5" : "#334155",
                }}
              >
                Total Budget
              </button>
            </div>
          </div>
          <div className="md:col-span-7">
            <label
              htmlFor="rr-wager"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
            >
              {wagerMode === "per_combo" ? "Amount per Combination (USD)" : "Total Budget (USD)"}
            </label>
            <div className="mt-2 relative">
              <span
                aria-hidden="true"
                className="absolute left-3.5 top-1/2 -translate-y-1/2 font-display text-slate-500 text-lg pointer-events-none select-none"
              >
                $
              </span>
              <input
                id="rr-wager"
                data-testid={ROUND_ROBIN.wagerInput}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                placeholder="0.00"
                value={wagerValue}
                onChange={(e) => setWagerValue(e.target.value)}
                data-invalid={visibleWagerErr ? "true" : "false"}
                aria-invalid={!!visibleWagerErr}
                aria-describedby={visibleWagerErr ? "rr-wager-error" : undefined}
                className="pa-input font-mono-num text-lg"
                style={{ paddingLeft: "2.25rem" }}
              />
            </div>
            {visibleWagerErr && (
              <p id="rr-wager-error" data-testid={ROUND_ROBIN.wagerError} className="pa-inline-error">
                {visibleWagerErr}
              </p>
            )}
            <p className="mt-2 text-xs text-slate-500">
              {wagerMode === "per_combo"
                ? `Total stake = ${totalCombos} combos × amount per combo.`
                : "Budget is split evenly across every combination to the nearest cent. The last combo absorbs any leftover pennies so the full budget is used."}
            </p>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl uppercase tracking-wide text-slate-900">
              Summary
            </h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyResults}
                disabled={showResultsDisabled}
                data-testid={ROUND_ROBIN.copyResults}
                className="pa-btn pa-btn-ghost !py-2 !text-xs disabled:opacity-40"
              >
                <Copy size={14} /> Copy
              </button>
              <button
                type="button"
                onClick={shareResults}
                disabled={showResultsDisabled}
                data-testid={ROUND_ROBIN.shareResults}
                className="pa-btn pa-btn-ghost !py-2 !text-xs disabled:opacity-40"
              >
                <Share2 size={14} /> Share
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="pa-result">
              <span className="pa-result-label">Combinations</span>
              <span className="pa-result-value font-mono-num" data-testid={ROUND_ROBIN.totalCombos}>
                {result ? result.totalCombos : totalCombos || "—"}
              </span>
            </div>
            <div className="pa-result">
              <span className="pa-result-label">Total Stake</span>
              <span className="pa-result-value font-mono-num" data-testid={ROUND_ROBIN.totalStake}>
                {result ? formatUSD(result.totalStake) : "—"}
              </span>
            </div>
            <div className="pa-result">
              <span className="pa-result-label">Max Potential Return</span>
              <span
                className="pa-result-value font-mono-num"
                data-testid={ROUND_ROBIN.totalMaxReturn}
              >
                {result ? formatUSD(result.totalMaxReturn) : "—"}
              </span>
            </div>
            <div className="pa-result pa-result-emerald">
              <span className="pa-result-label">Max Potential Profit</span>
              <span
                className="pa-result-value font-mono-num"
                data-testid={ROUND_ROBIN.totalMaxProfit}
              >
                {result ? formatUSD(result.totalMaxProfit) : "—"}
              </span>
            </div>
          </div>

          {result && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="pa-result">
                <span className="pa-result-label">Settled Return</span>
                <span className="pa-result-value font-mono-num" data-testid={ROUND_ROBIN.settledReturn}>
                  {formatUSD(result.settledReturn)}
                </span>
              </div>
              <div className="pa-result">
                <span className="pa-result-label">Settled Profit</span>
                <span
                  className="pa-result-value font-mono-num"
                  style={{ color: result.settledProfit >= 0 ? "#047857" : "#b91c1c" }}
                  data-testid={ROUND_ROBIN.settledProfit}
                >
                  {formatUSD(result.settledProfit)}
                </span>
              </div>
              <div className="pa-result">
                <span className="pa-result-label">Still Live Max</span>
                <span className="pa-result-value font-mono-num" data-testid={ROUND_ROBIN.pendingMax}>
                  {formatUSD(result.pendingReturnMax)}
                </span>
              </div>
            </div>
          )}

          {result && (
            <p className="mt-3 text-sm text-slate-600" data-testid={ROUND_ROBIN.overallStatus}>
              <span className="font-semibold">Status:</span>{" "}
              {result.overallStatus === "settled" ? "All combinations settled." : "Some combinations are still live."}
              {" "}
              {`Won ${result.comboCounts.won || 0} · Lost ${result.comboCounts.lost || 0} · Refunded ${
                result.comboCounts.pushed || 0
              } · Pending ${result.comboCounts.pending || 0}.`}
            </p>
          )}

          {!result && (
            <p className="mt-4 text-sm text-slate-500">
              Fill in every selection with valid American odds, pick at least one round-robin size,
              and enter a wager to see combinations and payouts.
            </p>
          )}
        </div>
      </section>

      {/* Combination Tables */}
      {result && (
        <section className="mt-8 space-y-6">
          {result.groups.map((group) => (
            <div
              key={group.size}
              data-testid={ROUND_ROBIN.groupSection(group.size)}
              className="pa-card p-5 md:p-6"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-display text-lg uppercase tracking-wide text-slate-900">
                  Round Robin By {group.size}
                </h3>
                <div className="text-xs text-slate-500 font-mono-num">
                  {group.count} combos · Stake {formatUSD(group.subtotalStake)}
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3">#</th>
                      <th className="py-2 pr-3">Legs</th>
                      <th className="py-2 pr-3">Combined</th>
                      <th className="py-2 pr-3">Stake</th>
                      <th className="py-2 pr-3">Max Return</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row, i) => (
                      <tr
                        key={i}
                        data-testid={ROUND_ROBIN.comboRow(group.size, i)}
                        className="border-b border-slate-100"
                      >
                        <td className="py-2 pr-3 font-mono-num text-slate-500">{i + 1}</td>
                        <td className="py-2 pr-3 font-mono-num text-slate-800">
                          {row.indices
                            .map((idx) => formatAmerican(cleanedLegs[idx]))
                            .join(" · ")}
                        </td>
                        <td className="py-2 pr-3 font-mono-num text-slate-800">
                          {formatAmerican(row.americanCombined)}{" "}
                          <span className="text-slate-400">
                            ({formatDecimal(row.decimalProduct)})
                          </span>
                        </td>
                        <td className="py-2 pr-3 font-mono-num">{formatUSD(row.stake)}</td>
                        <td className="py-2 pr-3 font-mono-num">{formatUSD(row.maxReturn)}</td>
                        <td className="py-2 pr-3">
                          <StatusBadge
                            status={row.grade.status}
                            testid={ROUND_ROBIN.comboStatus(group.size, i)}
                          />
                        </td>
                        <td
                          className="py-2 pr-3 font-mono-num"
                          data-testid={ROUND_ROBIN.comboReturn(group.size, i)}
                          style={{
                            color:
                              row.grade.status === "won"
                                ? "#047857"
                                : row.grade.status === "lost"
                                  ? "#b91c1c"
                                  : undefined,
                          }}
                        >
                          {row.grade.status === "pending"
                            ? `${formatUSD(row.grade.potentialReturn)} live`
                            : formatUSD(row.grade.return)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>
      )}

      <p className="mt-8 text-xs text-slate-500 max-w-3xl">
        New to round robins? Read the{" "}
        <Link to="/playbook/round-robin" className="underline hover:text-emerald-700">
          Round Robin lesson
        </Link>{" "}
        for a plain-English walkthrough. Parlay Assistant provides educational tools only and does
        not accept wagers.
      </p>
    </main>
  );
}
